export interface Theme {
  id: string;
  name: string;
  icon: string;
  headerImage?: string; // Optional image path for theme-specific header images
  backgroundImage?: string; // Optional background image path for theme-specific backgrounds
  colors: {
    '--pub-green': string;
    '--pub-green-light': string;
    '--pub-green-dark': string;
    '--wood-brown': string;
    '--wood-brown-light': string;
    '--wood-brown-dark': string;
    '--gold': string;
    '--gold-light': string;
    '--gold-dark': string;
    '--cream': string;
    '--warm-white': string;
    '--text-dark': string;
    '--text-medium': string;
    '--text-light': string;
    '--border-gold': string;
    '--shadow-brown': string;
  };
}

export const themes: Theme[] = [
  {
    id: 'classic-irish-pub',
    name: 'Classic Irish Pub',
    icon: '🍺',
    colors: {
      '--pub-green': '#2d5a3d',
      '--pub-green-light': '#3d7a4f',
      '--pub-green-dark': '#1e3a2a',
      '--wood-brown': '#8b4513',
      '--wood-brown-light': '#a0522d',
      '--wood-brown-dark': '#654321',
      '--gold': '#daa520',
      '--gold-light': '#ffd700',
      '--gold-dark': '#b8860b',
      '--cream': '#f5f5dc',
      '--warm-white': '#faf9f6',
      '--text-dark': '#2c3e50',
      '--text-medium': '#34495e',
      '--text-light': '#7f8c8d',
      '--border-gold': 'rgba(218, 165, 32, 0.3)',
      '--shadow-brown': 'rgba(139, 69, 19, 0.2)',
    },
  },
  {
    id: 'drinks-ahoy',
    name: 'Drinks Ahoy!',
    icon: '🏴‍☠️',
    headerImage: `${process.env.PUBLIC_URL || ''}/images/pirate-bartender.png`,
    backgroundImage: `${process.env.PUBLIC_URL || ''}/images/pirate-background.jpeg`,
    colors: {
      '--pub-green': '#1a4d3a',
      '--pub-green-light': '#2d6b4f',
      '--pub-green-dark': '#0f2e1f',
      '--wood-brown': '#8b4513',
      '--wood-brown-light': '#a0522d',
      '--wood-brown-dark': '#654321',
      '--gold': '#daa520',
      '--gold-light': '#ffd700',
      '--gold-dark': '#b8860b',
      '--cream': '#f5e6d3',
      '--warm-white': '#faf5f0',
      '--text-dark': '#2c3e50',
      '--text-medium': '#34495e',
      '--text-light': '#7f8c8d',
      '--border-gold': 'rgba(218, 165, 32, 0.4)',
      '--shadow-brown': 'rgba(139, 69, 19, 0.3)',
    },
  },
  {
    id: 'modern-dark',
    name: 'Modern Dark',
    icon: '🌙',
    colors: {
      '--pub-green': '#4a9b6e',
      '--pub-green-light': '#5fb88a',
      '--pub-green-dark': '#2d5a3d',
      '--wood-brown': '#3a3a3a',
      '--wood-brown-light': '#4a4a4a',
      '--wood-brown-dark': '#2a2a2a',
      '--gold': '#ffd700',
      '--gold-light': '#ffed4e',
      '--gold-dark': '#ccaa00',
      '--cream': '#1e1e1e',
      '--warm-white': '#2a2a2a',
      '--text-dark': '#e0e0e0',
      '--text-medium': '#b0b0b0',
      '--text-light': '#808080',
      '--border-gold': 'rgba(255, 215, 0, 0.3)',
      '--shadow-brown': 'rgba(0, 0, 0, 0.4)',
    },
  },
  {
    id: 'ocean-breeze',
    name: 'Ocean Breeze',
    icon: '🌊',
    colors: {
      '--pub-green': '#2c5f7c',
      '--pub-green-light': '#3d7fa0',
      '--pub-green-dark': '#1e3f54',
      '--wood-brown': '#5a7a8a',
      '--wood-brown-light': '#6a9aab',
      '--wood-brown-dark': '#4a5a6a',
      '--gold': '#4a9bc7',
      '--gold-light': '#6ab8e0',
      '--gold-dark': '#2a7ba0',
      '--cream': '#e6f2f7',
      '--warm-white': '#f0f8fc',
      '--text-dark': '#2c3e50',
      '--text-medium': '#34495e',
      '--text-light': '#7f8c8d',
      '--border-gold': 'rgba(74, 155, 199, 0.3)',
      '--shadow-brown': 'rgba(90, 122, 138, 0.2)',
    },
  },
];

const THEME_STORAGE_KEY = 'cocktail-recipes-theme';

export const getStoredTheme = (): string => {
  const stored = localStorage.getItem(THEME_STORAGE_KEY);
  return stored || 'classic-irish-pub';
};

export const setStoredTheme = (themeId: string): void => {
  localStorage.setItem(THEME_STORAGE_KEY, themeId);
};

export const getTheme = (themeId: string): Theme => {
  return themes.find(theme => theme.id === themeId) || themes[0];
};

export const applyTheme = (theme: Theme): void => {
  const root = document.documentElement;
  Object.entries(theme.colors).forEach(([property, value]) => {
    root.style.setProperty(property, value);
  });
  
  // Apply background image if provided
  if (theme.backgroundImage) {
    root.style.setProperty('--theme-background-image', `url(${theme.backgroundImage})`);
  } else {
    root.style.removeProperty('--theme-background-image');
  }
};

